import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const NotFound = () => {
    const { lang } = useLanguage();

    useEffect(() => {
        document.title = lang === 'tr' ? "404 – Sayfa Bulunamadı | Go Nova Medya" : "404 – Page Not Found | Go Nova";
    }, [lang]);

    return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', position: 'relative', overflow: 'hidden', paddingTop: '160px', paddingBottom: '80px', paddingLeft: '1rem', paddingRight: '1rem' }}>

            {/* Background elements */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-500/10 rounded-full blur-[120px] pointer-events-none" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '800px', height: '800px', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '50%', filter: 'blur(120px)', pointerEvents: 'none' }} />

            <div className="relative z-10 glass-panel" style={{ 
                position: 'relative', 
                zIndex: 10, 
                padding: '3rem', 
                width: '100%', 
                maxWidth: '600px', 
                minHeight: '620px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                textAlign: 'center', 
                border: '1px solid rgba(255, 255, 255, 0.1)', 
                borderRadius: '1.5rem', 
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' 
            }}>

                <h1 className="text-[120px] font-black leading-none bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 mb-4 opacity-90 select-none" style={{ fontSize: 'clamp(80px, 15vw, 120px)', fontWeight: 900, lineHeight: 1, background: 'linear-gradient(to right, #60a5fa, #a855f7, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '1rem', opacity: 0.9, userSelect: 'none' }}>
                    404
                </h1>

                <h2 className="text-gradient" style={{ fontSize: 'clamp(2rem, 6vw, 3rem)', fontWeight: 800, marginBottom: '1.5rem', lineHeight: 1.1 }}>
                    {lang === 'tr' ? 'Boşlukta mı Kayboldunuz?' : 'Lost in the Digital Void?'}
                </h2>

                <p className="lead-description" style={{ marginBottom: '2rem' }}>
                    {lang === 'tr' 
                        ? 'Görünüşe göre keşfedilmemiş bir alana ulaştınız. Bu sayfa mevcut değil, ama sorun değil—en iyi kaşifler bile zaman zaman yolunu kaybeder.' 
                        : "It seems you've ventured into uncharted territory. This page doesn't exist, but don't worry—even the best explorers get lost sometimes."
                    }
                </p>

                <p className="text-md mb-12 italic" style={{ fontSize: '1rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '3rem' }}>
                    {lang === 'tr'
                        ? <>"Her dolaşan kaybolmuş değildir... ama siz kayboldunuz." <span style={{ fontStyle: 'normal' }}>😉</span></>
                        : <>"Not all those who wander are lost... but you definitely are." <span style={{ fontStyle: 'normal' }}>😉</span></>
                    }
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-6" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '1.5rem' }}>
                    <Link
                        to="/"
                        style={{
                            position: 'relative',
                            padding: '1rem 2rem',
                            backgroundColor: 'white',
                            color: '#000000',
                            fontWeight: 800,
                            borderRadius: '0.75rem',
                            overflow: 'hidden',
                            boxShadow: '0 10px 15px -3px rgba(255, 255, 255, 0.1)',
                            transition: 'all 0.3s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            textDecoration: 'none'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(255, 255, 255, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(255, 255, 255, 0.1)';
                        }}
                    >
                        <Home size={20} />
                        <span>{lang === 'tr' ? 'Ana Sayfaya Dön' : 'Take Me Home'}</span>
                    </Link>

                    <a
                        href="mailto:hello@gonova.media"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            color: 'rgba(255, 255, 255, 0.6)',
                            padding: '1rem 1.5rem',
                            borderRadius: '0.75rem',
                            border: '1px solid transparent',
                            transition: 'all 0.3s',
                            textDecoration: 'none'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.borderColor = 'transparent';
                        }}
                    >
                        <Mail size={20} />
                        <span style={{ fontWeight: 600 }}>hello@gonova.media</span>
                    </a>
                </div>

                <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid rgba(255, 255, 255, 0.05)', fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                    {lang === 'tr' 
                        ? 'Bir hata olduğunu düşünüyorsanız, lütfen destek ekibimizle iletişime geçin.' 
                        : 'If you believe this is an error, please contact our support team.'
                    }
                </div>
            </div>
        </div>
    );
};

export default NotFound;
